import { db, isDexieEnabled } from './';

function getDatabaseSafe() {
    return db;
}

export class DatabaseCleaner {
    /**
     * Delete all data from a specific table
     * @param tableName - Name of the table to clear
     * @returns Promise<void>
     */
    static async clearTable(tableName: 'analytics' | 'holdings' | 'exchangeKeys' | 'snapshots' | 'selectedExchange'): Promise<void> {
        if (!isDexieEnabled()) return; // Nothing to clear when the cache is disabled
        try {
            const database = getDatabaseSafe();
            await database[tableName].clear();
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete all data from the database
     * @param options - Configuration options for the deletion
     * @returns Promise<boolean>
     */
    static async clearDatabase(options: {
        clearAnalytics?: boolean;
        clearHoldings?: boolean;
        clearKeys?: boolean;
        clearSnapshots?: boolean;
        backup?: boolean;
    } = {
            clearAnalytics: true,
            clearHoldings: true,
            clearKeys: true,
            clearSnapshots: true,
            backup: true,
        }): Promise<boolean> {
        if (!isDexieEnabled()) return true; // Nothing to clear when the cache is disabled
        try {
            const database = getDatabaseSafe();

            // Create backup if requested
            if (options.backup) {
                const backup = {
                    timestamp: new Date().toISOString(),
                    analytics: await database.analytics.toArray(),
                    holdings: await database.holdings.toArray(),
                    exchangeKeys: await database.exchangeKeys.toArray(),
                    snapshots: await database.snapshots.toArray(),
                };

                // Save backup to localStorage (or elsewhere if needed)
                localStorage.setItem(
                    `aryacrypto_backup_${backup.timestamp}`,
                    JSON.stringify(backup)
                );
            }

            // Start transaction for atomic operation
            await database.transaction('rw', [database.analytics, database.holdings, database.exchangeKeys, database.snapshots], async () => {
                if (options.clearAnalytics) {
                    await this.clearTable('analytics');
                }
                if (options.clearHoldings) {
                    await this.clearTable('holdings');
                }
                if (options.clearKeys) {
                    await this.clearTable('exchangeKeys');
                }
                if (options.clearSnapshots) {
                    await this.clearTable('snapshots');
                }
            });

            return true;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get list of available backups
     * @returns Array of backup timestamps
     */
    static getAvailableBackups(): string[] {
        return Object.keys(localStorage)
            .filter(key => key.startsWith('aryacrypto_backup_'))
            .map(key => key.replace('aryacrypto_backup_', ''));
    }

    /**
     * Delete old backups
     * @param olderThan - Date to delete backups older than
     * @returns Promise<number> - Number of backups deleted
     */
    static async clearOldBackups(olderThan: Date): Promise<number> {
        let deletedCount = 0;
        const backups = this.getAvailableBackups();

        backups.forEach(timestamp => {
            if (new Date(timestamp) < olderThan) {
                localStorage.removeItem(`aryacrypto_backup_${timestamp}`);
                deletedCount++;
            }
        });

        return deletedCount;
    }
}
